import React, { useEffect, useState } from 'react';
import '@/assets/app/css/chapter.css';
import AppButton from '@/shared/components/ui/new/AppButton';
import useAppStore from '@/shared/store/UseAppStore';
import Quiz from './Quiz';
import { getUploadsUrl } from '@/shared/utils/CommonUtil';
import FrameBox from '@/shared/components/ui/new/FrameBox';

interface ChapterProps {
  chapter?: ChapterData;
  onNext?: () => void;
  onBack?: () => void;
  /** true면 인물 소개 없이 바로 완료된 결과 화면으로 이동 */
  viewResultOnly?: boolean;
}

const Chapter: React.FC<ChapterProps> = ({ chapter, onNext, onBack, viewResultOnly = false }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [stage, setStage] = useState<'chapter' | 'transition' | 'quiz'>(
    viewResultOnly ? 'quiz' : 'chapter',
  );
  const totalPages = chapter?.introduces?.length || 1;
  const { setIsShowBottomNavigation } = useAppStore();

  const handleNext = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    } else {
      setStage('quiz');
    }
  };

  const renderContent = () => {
    return (
      <>
        <div style={{ color: '#4B4B4B' }}>
          <div className="flex">
            <div className="flex font-bold" style={{ width: 72, flexShrink: 0 }}>
              한줄소개 :
            </div>
            <div className="flex" style={{ fontSize: 12 }}>
              {chapter?.oneLineIntro}
            </div>
          </div>
          <div className="flex">
            <div className="flex font-bold" style={{ width: 72, flexShrink: 0 }}>
              대표업적 :
            </div>
            <div className="flex" style={{ fontSize: 12 }}>
              {chapter?.achievement1}
              {chapter?.achievement1 && chapter?.achievement2 && <br />} {chapter?.achievement2}
            </div>
          </div>
          <div style={{ whiteSpace: 'pre-wrap', color: '#000', marginTop: 20, fontSize: 14 }}>
            {chapter?.introduces[currentPage]?.introductionText}
          </div>
        </div>
      </>
    );
  };

  useEffect(() => {
    setIsShowBottomNavigation(false);

    // cleanup 함수: 컴포넌트 언마운트 시 실행
    return () => {
      setIsShowBottomNavigation(true);
    };
  }, []);
  return (
    <>
      <div style={{ width: '100%', height: '100%', backgroundColor: '#fff', paddingBottom: 40 }}>
        {stage === 'chapter' && (
          <>
            <div className="historical-figure-container">
              {/* Header */}
              <header className="grid grid-cols-[1fr_auto_1fr] items-center w-full ">
                {/* 1. 왼쪽: 뒤로가기 버튼 영역 (왼쪽 정렬) */}
                <div className="flex justify-start">
                  <button className="btn-back-theme f-one-pop" onClick={onBack}>
                    뒤로가기
                  </button>
                </div>

                {/* 2. 가운데: 타이틀 영역 (양쪽 버튼 크기와 상관없이 항상 정중앙 고정) */}
                <h1 className="title">{chapter?.personName}</h1>

                {/* 3. 오른쪽: 챕터 표시 영역 (오른쪽 정렬) */}
                <div className="flex justify-end">
                  <span
                    className="text-[14px] font-medium"
                    style={{ color: 'var(--color-btn-border)' }}
                  >
                    {chapter?.chapterTitle}
                  </span>
                </div>
              </header>

              <div className="period">{chapter?.eraText}</div>

              {/* Character Image */}
              {chapter?.personIntroImage && (
                <div className="character-image-container">
                  <img
                    style={{ borderRadius: 10 }}
                    src={`${getUploadsUrl()}/person/${chapter?.personIntroImage}`}
                    alt={chapter?.personName}
                  />
                </div>
              )}

              {/* Title */}
              <h2 className="content-title" style={{ color: 'var(--color-btn-border)' }}>
                {chapter?.keywords}
              </h2>

              <FrameBox style={{ height: 320 }}>{renderContent()}</FrameBox>

              {/* Page Indicators */}
              <div className="page-indicators">
                {[...Array(totalPages)].map((_, index) => (
                  <div
                    key={index}
                    className={`indicator ${index === currentPage ? 'active' : ''}`}
                  />
                ))}
              </div>

              {/* Next Button */}
              <div className="bottom-btn-group mt-10">
                <AppButton
                  onConfirm={handleNext}
                  backgroundColor="var(--color-btn)"
                  boxShadowColor="var(--color-btn-border)"
                  className="f-one-pop"
                  message={`${totalPages === currentPage + 1 ? '퀴즈풀기' : '다음'}`}
                />
              </div>
            </div>
          </>
        )}
        {stage === 'quiz' && (
          <Quiz chapter={chapter} onClose={onBack} viewResultOnly={viewResultOnly} />
        )}
      </div>
    </>
  );
};

export default Chapter;
